import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ThemeToggle from './components/ThemeToggle';
import Login from './components/Login';
import JoinFamilyPage from './pages/JoinFamilyPage';
import HomePage from './pages/HomePage';
import FamiliesPage from './pages/FamiliesPage';
import FamilyDetailPage from './pages/FamilyDetailPage';
import PeoplePage from './pages/PeoplePage';
import PersonDetailPage from './pages/PersonDetailPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import AboutPage from './pages/AboutPage';
import AdminPage from './pages/AdminPage';
import ReportsPage from './pages/ReportsPage';
import D3TreePage from './pages/D3TreePage';
import './App.css';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

const Navigation = () => {
  const location = useLocation();
  const { theme } = useTheme();
  const { isAuthenticated, user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [deferredPrompt, setDeferredPrompt] = React.useState(null);
  const [showInstallPrompt, setShowInstallPrompt] = React.useState(false);

  // Add admin link if user is admin
  // The /api/ft/me endpoint returns { user: { is_admin: ... }, auth: {...} }
  // The login response returns { user: { is_admin: ... }, is_admin: ..., user_id: ... }
  const isAdmin = user?.user?.is_admin === true || user?.is_admin === true;
  
  // Create navItems array reactively based on user state
  const navItems = React.useMemo(() => {
    const items = [
      { path: '/', label: 'Home', icon: '🏠' },
      { path: '/families', label: 'Families', icon: '👨‍👩‍👧‍👦' },
      { path: '/people', label: 'Members', icon: '👤' },
      { path: '/tree', label: 'Tree View', icon: 'logo' },
      { path: '/reports', label: 'Reports', icon: '📈' },
    ];
    
    if (isAdmin) {
      items.push({ path: '/admin', label: 'Admin', icon: '👑' });
    }
    
    return items;
  }, [isAdmin]);

  // Handle PWA install prompt
  React.useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      // Prevent Chrome 67 and earlier from automatically showing the prompt
      e.preventDefault();
      // Stash the event so it can be triggered later
      setDeferredPrompt(e);
      setShowInstallPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Check if app is already installed
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) {
      setShowInstallPrompt(false);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      // Fallback: Show instructions for manual installation
      alert('To install this app:\n\n' +
        'iOS: Tap the Share button and select "Add to Home Screen"\n\n' +
        'Android: Tap the menu (⋮) and select "Install app" or "Add to Home screen"');
      return;
    }

    // Show the install prompt
    deferredPrompt.prompt();

    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      setShowInstallPrompt(false);
    }

    // Clear the deferredPrompt
    setDeferredPrompt(null);
  };

  // Close mobile menu when route changes
  React.useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  return (
    <>
      <nav
        style={{
          background: theme.glass.background,
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          borderBottom: theme.glass.border,
          padding: '12px 16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          boxShadow: theme.glass.shadow,
        }}
      >
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flex: 1, minWidth: 0 }}>
          <Link
            to="/"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              textDecoration: 'none',
              color: theme.text.primary,
              minWidth: 0,
            }}
          >
            <img
              src="/assets/family-tree-logo.png"
              alt="Family Tree"
              style={{
                height: '32px',
                width: 'auto',
                objectFit: 'contain',
                display: 'block',
                flexShrink: 0,
              }}
              onError={(e) => {
                // Hide image if logo file is not found
                e.target.style.display = 'none';
              }}
            />
            <span style={{ 
              fontSize: '18px', 
              fontWeight: 'bold',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
              Family Tree
            </span>
          </Link>
          {/* Desktop Navigation */}
          <div style={{ 
            gap: '8px', 
            alignItems: 'center',
            flex: 1,
          }}
          className="desktop-nav"
          >
            {navItems.filter(item => item.path !== '/').map((item) => (
              <Link
                key={item.path}
                to={item.path}
                style={{
                  padding: '8px 12px',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  color: location.pathname === item.path ? theme.accent.primary : theme.text.secondary,
                  background: location.pathname === item.path ? theme.card.background : 'transparent',
                  border: location.pathname === item.path ? theme.glass.border : '1px solid transparent',
                  transition: 'all 0.3s ease',
                  fontSize: '14px',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  whiteSpace: 'nowrap',
                }}
              >
                {item.icon === 'logo' ? (
                  <img
                    src="/assets/family-tree-logo.png"
                    alt="Tree View"
                    style={{
                      height: '18px',
                      width: 'auto',
                      objectFit: 'contain',
                    }}
                  />
                ) : (
                  item.icon
                )}{' '}
                {item.label}
              </Link>
            ))}
          </div>
        </div>
        
        {/* Desktop User Actions */}
        <div style={{ 
          gap: '12px', 
          alignItems: 'center',
          flexShrink: 0,
        }}
        className="desktop-user-actions"
        >
          {isAuthenticated && user && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {user.user?.avatar_url && (
                <img
                  src={user.user.avatar_url}
                  alt={user.user.full_name || user.auth?.email}
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    border: theme.glass.border,
                    flexShrink: 0,
                  }}
                />
              )}
              {user.user?.claimed_person_id ? (
                <Link
                  to={`/people/${user.user.claimed_person_id}`}
                  style={{
                    color: theme.text.secondary,
                    fontSize: '14px',
                    textDecoration: 'none',
                    transition: 'color 0.3s ease',
                    whiteSpace: 'nowrap',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = theme.accent.primary;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = theme.text.secondary;
                  }}
                >
                  {user.user?.full_name || user.auth?.email}
                </Link>
              ) : (
                <span style={{ color: theme.text.secondary, fontSize: '14px', whiteSpace: 'nowrap' }}>
                  {user.user?.full_name || user.auth?.email}
                </span>
              )}
              <button
                onClick={logout}
                style={{
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: theme.glass.border,
                  background: 'transparent',
                  color: theme.text.secondary,
                  fontSize: '14px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  minHeight: '44px',
                  whiteSpace: 'nowrap',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = theme.card.background;
                  e.currentTarget.style.color = theme.text.primary;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = theme.text.secondary;
                }}
              >
                Logout
              </button>
            </div>
          )}
          {!isAuthenticated && location.pathname !== '/login' && (
            <Link
              to="/login"
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                textDecoration: 'none',
                color: theme.accent.primary,
                background: theme.card.background,
                border: theme.glass.border,
                fontSize: '14px',
                fontWeight: 500,
                minHeight: '44px',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              Sign In
            </Link>
          )}
          <ThemeToggle />
        </div>

        {/* Mobile Hamburger Menu Button */}
        <div 
          className="mobile-nav-controls"
          style={{ 
            gap: '8px',
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          <ThemeToggle />
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            style={{
              padding: '8px',
              borderRadius: '6px',
              border: theme.glass.border,
              background: mobileMenuOpen ? theme.card.background : 'transparent',
              color: theme.text.primary,
              fontSize: '24px',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              minWidth: '44px',
              minHeight: '44px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? '✕' : '☰'}
          </button>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 999,
            animation: 'fadeIn 0.3s ease',
          }}
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Menu */}
      <div
        style={{
          position: 'fixed',
          top: '60px',
          left: mobileMenuOpen ? 0 : '-100%',
          right: 0,
          bottom: 0,
          background: theme.glass.background,
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          borderRight: theme.glass.border,
          zIndex: 1000,
          transition: 'left 0.3s ease',
          overflowY: 'auto',
          maxWidth: '300px',
          boxShadow: theme.glass.shadow,
        }}
        className="mobile-menu"
      >
        <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '8px', minHeight: '100%' }}>
          {/* Mobile Navigation Items */}
          {navItems && navItems.length > 0 ? navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setMobileMenuOpen(false)}
              style={{
                padding: '16px',
                borderRadius: '8px',
                textDecoration: 'none',
                color: location.pathname === item.path ? theme.accent.primary : theme.text.primary,
                background: location.pathname === item.path ? theme.card.background : 'transparent',
                border: location.pathname === item.path ? theme.glass.border : '1px solid transparent',
                transition: 'all 0.3s ease',
                fontSize: '16px',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                minHeight: '44px',
              }}
            >
              {item.icon === 'logo' ? (
                <img
                  src="/assets/family-tree-logo.png"
                  alt="Tree View"
                  style={{
                    height: '24px',
                    width: 'auto',
                    objectFit: 'contain',
                  }}
                />
              ) : (
                <span style={{ fontSize: '20px' }}>{item.icon}</span>
              )}
              {item.label}
            </Link>
          )) : (
            <div style={{ padding: '16px', color: theme.text.secondary, textAlign: 'center' }}>
              No menu items available
            </div>
          )}

          {/* Install to Home Screen Option */}
          {(showInstallPrompt || deferredPrompt) && (
            <button
              onClick={handleInstallClick}
              style={{
                padding: '16px',
                borderRadius: '8px',
                border: theme.glass.border,
                background: theme.accent.primary + '15',
                color: theme.accent.primary,
                fontSize: '16px',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                minHeight: '44px',
                textAlign: 'left',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginTop: '16px',
                borderTop: theme.glass.border,
                paddingTop: '16px',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = theme.accent.primary + '25';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = theme.accent.primary + '15';
              }}
            >
              <span style={{ fontSize: '20px' }}>📱</span>
              <span>Install App</span>
            </button>
          )}

          {/* Mobile User Info */}
          {isAuthenticated && user && (
            <>
              <div style={{ 
                marginTop: '16px', 
                paddingTop: '16px', 
                borderTop: theme.glass.border,
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '0 16px' }}>
                  {user.user?.avatar_url && (
                    <img
                      src={user.user.avatar_url}
                      alt={user.user.full_name || user.auth?.email}
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        border: theme.glass.border,
                      }}
                    />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {user.user?.claimed_person_id ? (
                      <Link
                        to={`/people/${user.user.claimed_person_id}`}
                        onClick={() => setMobileMenuOpen(false)}
                        style={{
                          color: theme.text.primary,
                          fontSize: '14px',
                          textDecoration: 'none',
                          fontWeight: 600,
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {user.user?.full_name || user.auth?.email}
                      </Link>
                    ) : (
                      <span style={{ color: theme.text.primary, fontSize: '14px', fontWeight: 600, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {user.user?.full_name || user.auth?.email}
                      </span>
                    )}
                    <span style={{ color: theme.text.secondary, fontSize: '12px' }}>Logged in</span>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    logout();
                  }}
                  style={{
                    padding: '16px',
                    borderRadius: '8px',
                    border: theme.glass.border,
                    background: 'transparent',
                    color: theme.text.primary,
                    fontSize: '16px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    minHeight: '44px',
                    textAlign: 'left',
                  }}
                >
                  Logout
                </button>
              </div>
            </>
          )}
          {!isAuthenticated && location.pathname !== '/login' && (
            <Link
              to="/login"
              onClick={() => setMobileMenuOpen(false)}
              style={{
                padding: '16px',
                borderRadius: '8px',
                textDecoration: 'none',
                color: theme.accent.primary,
                background: theme.card.background,
                border: theme.glass.border,
                fontSize: '16px',
                fontWeight: 500,
                textAlign: 'center',
                marginTop: '16px',
                minHeight: '44px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              Sign In
            </Link>
          )}
        </div>
      </div>
    </>
  );
};

// Protected route component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <p>Loading...</p>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

function App() {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <ThemeProvider>
        <AuthProvider>
          <Router>
            <AppContent />
          </Router>
        </AuthProvider>
      </ThemeProvider>
    </GoogleOAuthProvider>
  );
}

const AppContent = () => {
  const { theme } = useTheme();

  return (
    <div
      style={{
        minHeight: '100vh',
        background: theme.background,
        color: theme.text.primary,
        transition: 'background 0.3s ease, color 0.3s ease',
      }}
    >
      <Navigation />
      <main style={{ padding: '32px', maxWidth: '1400px', margin: '0 auto', minHeight: 'calc(100vh - 200px)', width: '100%' }} className="main-content">
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/join/:code" element={<JoinFamilyPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route path="/" element={<HomePage />} />
          <Route
            path="/families"
            element={
              <ProtectedRoute>
                <FamiliesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/families/:id"
            element={
              <ProtectedRoute>
                <FamilyDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/people"
            element={
              <ProtectedRoute>
                <PeoplePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/people/:id"
            element={
              <ProtectedRoute>
                <PersonDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tree"
            element={
              <ProtectedRoute>
                <D3TreePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <ProtectedRoute>
                <ReportsPage />
              </ProtectedRoute>
            }
          />
        </Routes>
      </main>
      <Footer />
    </div>
  );
};

const Footer = () => {
  const { theme } = useTheme();
  const location = useLocation();
  
  // Don't show footer on login page
  if (location.pathname === '/login') {
    return null;
  }

  return (
    <footer
      style={{
        marginTop: '32px',
        padding: '20px 16px',
        borderTop: theme.glass.border,
        background: theme.glass.background,
        backdropFilter: 'blur(10px)',
        textAlign: 'center',
      }}
    >
      <p style={{ color: theme.text.muted, fontSize: 'clamp(12px, 2vw, 14px)', marginBottom: '12px' }}>
        Our Family Tree by Surya (Chanti) Manepalli
      </p>
      <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap', alignItems: 'center' }}>
        <Link
          to="/about"
          style={{
            color: theme.text.secondary,
            textDecoration: 'none',
            fontSize: 'clamp(13px, 2vw, 14px)',
            transition: 'color 0.3s ease',
            padding: '4px 8px',
            minHeight: '44px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = theme.accent.primary;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = theme.text.secondary;
          }}
        >
          About
        </Link>
        <span style={{ color: theme.text.muted, fontSize: '14px' }}>•</span>
        <Link
          to="/privacy"
          style={{
            color: theme.text.secondary,
            textDecoration: 'none',
            fontSize: 'clamp(13px, 2vw, 14px)',
            transition: 'color 0.3s ease',
            padding: '4px 8px',
            minHeight: '44px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = theme.accent.primary;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = theme.text.secondary;
          }}
        >
          Privacy Policy
        </Link>
        <span style={{ color: theme.text.muted, fontSize: '14px' }}>•</span>
        <Link
          to="/terms"
          style={{
            color: theme.text.secondary,
            textDecoration: 'none',
            fontSize: 'clamp(13px, 2vw, 14px)',
            transition: 'color 0.3s ease',
            padding: '4px 8px',
            minHeight: '44px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = theme.accent.primary;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = theme.text.secondary;
          }}
        >
          Terms of Service
        </Link>
      </div>
    </footer>
  );
};

export default App;

